import type { FC } from "react"
import { matchPath, useLocation } from "react-router"
import { Helmet } from "react-helmet-async"

interface Props {
  title?: string
  description?: string
}

const TITLE_PREFIX = "WarpMe 運営管理画面"

const resolveDefaultTitle = (pathname: string) => {
  const routeTitles = [
    { path: "/login", title: "ログイン" },
    { path: "/dashboard", title: "ダッシュボード" },
    { path: "/restaurants", title: "店舗一覧" },
    { path: "/restaurants/:restaurantId/sales", title: "販売状況" },
    { path: "/restaurants/:restaurantId/info", title: "店舗情報" },
    { path: "/restaurants/:restaurantId/shops/:shopId/tickets", title: "チケット情報" },
    {
      path: "/restaurants/:restaurantId/shops/:shopId/tickets/:ticketId",
      title: "チケット詳細",
    },
    { path: "/restaurants/:restaurantId/sales-fee", title: "販売手数料" },
    { path: "/transactions", title: "取引一覧" },
    { path: "/transactions/:purchaseId", title: "取引詳細" },
    { path: "/setting/members", title: "メンバー" },
    { path: "/setting/members/new", title: "メンバー新規作成" },
    { path: "/setting/members/:memberId", title: "メンバー詳細" },
    { path: "/setting/terms", title: "規約" },
  ]

  return routeTitles.find(({ path }) => matchPath({ path, end: true }, pathname))?.title
}

export const MetaTags: FC<Props> = ({ title, description }) => {
  const { pathname } = useLocation()
  const resolvedTitle = title ?? resolveDefaultTitle(pathname) ?? TITLE_PREFIX
  const resolvedDescription =
    description ??
    (pathname === "/not-found"
      ? "指定されたページは存在しないか、移動した可能性があります。"
      : "WarpMeの店舗、チケット、取引、設定を管理するための運営コンソールです。")

  return (
    <Helmet>
      <title>
        {resolvedTitle === TITLE_PREFIX ? TITLE_PREFIX : `${TITLE_PREFIX} | ${resolvedTitle}`}
      </title>
      <meta name="description" content={resolvedDescription} />
      <meta property="og:site_name" content={TITLE_PREFIX} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
    </Helmet>
  )
}
